/**
 * AuthGuard Component - FIXED to prevent infinite loops
 * Enhanced with employee preview mode support
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import useSessionTimeout from '../../hooks/useSessionTimeout';
import LoadingSpinner from '../common/LoadingSpinner';

const AuthGuard = ({ children, requireStore = true, customerOnly = false }) => {
  useSessionTimeout();
  const location = useLocation();
  const {
    isAuthenticated,
    isInitialized,
    isLoading,
    hasStoreAccess,
    needsStoreSelection,
    store,
    company,
    logout,
    isEmployee
  } = useAuth();

  // Show loading while initializing
  if (!isInitialized || isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-3 text-muted">Initializing...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Block employees from customer-only routes (cart, checkout, orders, profile)
  if (customerOnly && isEmployee()) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card border-0 shadow">
              <div className="card-body p-5 text-center">
                <div className="mb-4">
                  <i className="bi bi-eye text-info" style={{ fontSize: '3rem' }}></i>
                </div>
                <h3 className="card-title mb-3">Preview Mode</h3>
                <p className="card-text text-muted mb-4">
                  This feature is not available in employee preview mode.
                  You can browse products, categories, and brands.
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => window.history.back()}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User has no store/company access - show error
  if (!hasStoreAccess()) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card border-0 shadow">
              <div className="card-body p-5 text-center">
                <div className="mb-4">
                  <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                </div>
                <h3 className="card-title mb-3">
                  {isEmployee() ? 'No Company Access' : 'No Store Access'}
                </h3>
                <p className="card-text text-muted mb-4">
                  {isEmployee()
                    ? "Your employee account doesn't have access to any companies yet. Please contact your administrator."
                    : "Your account doesn't have access to any stores yet. Please contact your administrator to get store access assigned."}
                </p>
                <div className="d-flex gap-3 justify-content-center">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => window.location.reload()}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => logout()}
                  >
                    <i className="bi bi-box-arrow-left me-2"></i>
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Store/company selection logic
  if (requireStore) {
    if (isEmployee()) {
      // Employees need a company selected (not a store)
      if (!company) {
        return <Navigate to="/account" replace />;
      }
    } else {
      // Customers need a store selected
      if (needsStoreSelection()) {
        return <Navigate to="/account" replace />;
      }
      if (!store) {
        return <Navigate to="/account" replace />;
      }
    }
  }

  // User is authenticated and has required access
  return children;
};

export default AuthGuard;